<template>
  <section :class="sectionClasses">
    <div class="container">
      <div v-if="title || subtitle || $slots.header" class="base-section__header">
        <slot name="header">
          <h2 v-if="title" class="base-section__title">{{ title }}</h2>
          <p v-if="subtitle" class="base-section__subtitle">{{ subtitle }}</p>
        </slot>
      </div>
      
      <div class="base-section__content">
        <slot />
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: 'BaseSection',
  props: {
    title: {
      type: String,
      default: null
    },
    subtitle: {
      type: String,
      default: null
    },
    background: {
      type: String,
      default: 'white',
      validator: (value) => ['white', 'light', 'primary', 'secondary', 'gradient'].includes(value)
    },
    spacing: {
      type: String,
      default: 'normal',
      validator: (value) => ['small', 'normal', 'large'].includes(value)
    },
    centered: {
      type: Boolean,
      default: true
    }
  },
  computed: {
    sectionClasses() {
      return [
        'base-section',
        `base-section--bg-${this.background}`,
        `base-section--spacing-${this.spacing}`,
        {
          'base-section--centered': this.centered
        }
      ];
    }
  }
};
</script>

<style lang="scss" scoped>
@import '@/styles/variables.scss';
@import '@/styles/mixins.scss';

.base-section {
  position: relative;
  overflow: hidden;
  
  &--bg-white {
    background-color: $white;
  }
  
  &--bg-light {
    background-color: $gray-100;
  }
  
  &--bg-primary {
    background-color: $primary-color;
    color: $white;
    
    .base-section__title,
    .base-section__subtitle {
      color: $white;
    }
  }
  
  &--bg-secondary {
    background-color: $secondary-light;
  }
  
  &--bg-gradient {
    @include gradient-primary;
    color: $white;
    
    .base-section__title,
    .base-section__subtitle {
      color: $white;
    }
  }
  
  &--spacing-small {
    padding: $spacing-3xl 0;
    
    @include media-breakpoint-down(md) {
      padding: $spacing-2xl 0;
    }
  }
  
  &--spacing-normal {
    padding: $spacing-4xl 0;
    
    @include media-breakpoint-down(md) {
      padding: $spacing-3xl 0;
    }
  }
  
  &--spacing-large {
    padding: calc(#{$spacing-4xl} * 1.5) 0;
    
    @include media-breakpoint-down(md) {
      padding: $spacing-4xl 0;
    }
  }
  
  &--centered {
    .base-section__header {
      text-align: center;
    }
  }
  
  &__header {
    margin-bottom: $spacing-3xl;
    
    @include media-breakpoint-down(md) {
      margin-bottom: $spacing-2xl;
    }
  }
  
  &__title {
    font-size: $font-size-4xl;
    font-weight: 700;
    margin-bottom: $spacing-md;
    color: $primary-color;
    position: relative;
    
    @include media-breakpoint-down(md) {
      font-size: $font-size-3xl;
    }
    
    .base-section--centered & {
      &::after {
        content: '';
        display: block;
        width: 60px;
        height: 3px;
        background-color: $secondary-color;
        margin: $spacing-lg auto 0;
      }
    }
  }
  
  &__subtitle {
    font-size: $font-size-lg;
    color: $gray-600;
    max-width: 600px;
    margin: 0 auto;
    line-height: 1.7;
  }
  
  &__content {
    position: relative;
  }
}</style>